export type DriverGradeTone = 'GOOD' | 'NEUTRAL' | 'POOR'

export const TONE_COLORS: Record<DriverGradeTone, string> = {
  GOOD: '#059669',
  NEUTRAL: '#d97706',
  POOR: '#e11d48',
}

export const TONE_PILL_CLASSES: Record<DriverGradeTone, string> = {
  GOOD: 'border-emerald-300 bg-emerald-100 text-emerald-900',
  NEUTRAL: 'border-amber-300 bg-amber-100 text-amber-900',
  POOR: 'border-rose-300 bg-rose-100 text-rose-900',
}

// On the grade's own scale, which runs from about 50 to the maximum grade.
const GOOD_GRADE = 85
const POOR_GRADE = 70

export const gradeTone = (grade: number): DriverGradeTone =>
  grade >= GOOD_GRADE ? 'GOOD' : grade < POOR_GRADE ? 'POOR' : 'NEUTRAL'

export interface MetricBand {
  bandLow: number
  bandHigh: number
  higherIsBetter: boolean
}

/** A value's 0–100 score against the metric's band, as the backend scores it. */
export const metricScore = (value: number, band: MetricBand): number => {
  const ratio = (value - band.bandLow) / (band.bandHigh - band.bandLow)
  const oriented = band.higherIsBetter ? ratio : 1 - ratio
  return Math.round(Math.min(1, Math.max(0, oriented)) * 1000) / 10
}

export const weightPercent = (weight: number, weights: number[]): number => {
  const total = weights.reduce((sum, w) => sum + w, 0)
  return total ? Math.round((weight / total) * 100) : 0
}

interface SegmentMetric {
  key: string
  tone: DriverGradeTone | null
}

interface Segment {
  datetimeStart: string
  datetimeEnd: string
  metrics: SegmentMetric[]
}

export interface HighlightSpan {
  start: Date
  end: Date
  color: string
}

/** Every segment the metric was measured on, coloured by how it scored there. */
export const metricHighlights = (
  segments: Segment[],
  key: string
): HighlightSpan[] =>
  segments.flatMap(segment => {
    const tone = segment.metrics.find(metric => metric.key === key)?.tone
    if (!tone) return []

    return [
      {
        start: new Date(segment.datetimeStart),
        end: new Date(segment.datetimeEnd),
        color: TONE_COLORS[tone],
      },
    ]
  })

// A unit that kept no end time was still over the limit when it was read.
const OPEN_EVENT_MS = 60_000
// Positions arrive minutes apart, so a minute-long event can fall between two:
// widened this much it reaches the positions on either side.
const EVENT_PADDING_MS = 2 * 60_000

interface TimedEvent {
  datetimeStart: string
  datetimeEnd?: string | null
}

/** The overspeed events as spans on the route, all in the poor colour. */
export const eventHighlights = (
  events: TimedEvent[],
  paddingMs = EVENT_PADDING_MS
): HighlightSpan[] =>
  events.map(event => {
    const start = new Date(event.datetimeStart).getTime()
    const end = event.datetimeEnd
      ? new Date(event.datetimeEnd).getTime()
      : start + OPEN_EVENT_MS
    return {
      start: new Date(start - paddingMs),
      end: new Date(end + paddingMs),
      color: TONE_COLORS.POOR,
    }
  })

export const formatNumber = (value: number, digits = 1): string =>
  value.toLocaleString('nb-NO', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
